import React, { useState, useEffect } from 'react';
import { accountingApi } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { DragDropZone } from '../components/DragDropZone';
import ExpensesTab from '../components/ExpensesTab';
import BankReconciliation from '../components/BankReconciliation';
import CustomSettlementDialog from '../components/CustomSettlementDialog';
import OrphanLoadsPanel from '../components/OrphanLoadsPanel';
import {
  DollarSign, TrendingUp, TrendingDown, Truck, Upload, FileText, AlertCircle,
  CheckCircle, Loader2, BarChart3, Wallet, PiggyBank, Receipt, FileEdit,
} from 'lucide-react';
import { toast } from 'sonner';

const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const currentYear = new Date().getFullYear();

function fmt$(n) { return '$' + Math.abs(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }); }

export default function Accounting() {
  const [tab, setTab] = useState('overview');
  const [period, setPeriod] = useState('ytd');
  const [year, setYear] = useState(currentYear);
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [quarter, setQuarter] = useState(Math.ceil((new Date().getMonth() + 1) / 3));
  const [summary, setSummary] = useState(null);
  const [unitBreakdown, setUnitBreakdown] = useState([]);
  const [csOpen, setCsOpen] = useState(false);
  const [csUnit, setCsUnit] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [catDialogOpen, setCatDialogOpen] = useState(false);
  const [newCatName, setNewCatName] = useState('');
  const [expensesTotal, setExpensesTotal] = useState(0);

  const params = { period, year, ...(period === 'monthly' ? { month } : {}), ...(period === 'quarterly' ? { quarter } : {}) };

  useEffect(() => { loadData(); }, [period, year, month, quarter]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [sumRes, unitRes, catRes] = await Promise.all([
        accountingApi.getSummary(params),
        accountingApi.getUnitBreakdown(params),
        accountingApi.getCategories(),
      ]);
      setSummary(sumRes.data);
      setUnitBreakdown(unitRes.data);
      setCategories(catRes.data);

      // Load transactions
      const txRes = await accountingApi.getTransactions({ period_start: sumRes.data.start_date, period_end: sumRes.data.end_date });
      setTransactions(txRes.data);

      // Load expenses summary for the same period so the Overview shows expense totals
      try {
        const { expensesApi } = await import('../lib/api');
        const expRes = await expensesApi.getSummary(sumRes.data.start_date, sumRes.data.end_date);
        setExpensesTotal(expRes.data?.total || 0);
      } catch {
        setExpensesTotal(0);
      }
    } catch { toast.error('Failed to load accounting data'); }
    finally { setLoading(false); }
  };

  const handleUpload = async () => {
    if (!uploadFile) return;
    setUploading(true);
    try {
      const res = await accountingApi.uploadStatement(uploadFile, summary?.start_date || '', summary?.end_date || '');
      const jobId = res.data.job_id;
      toast.success('Processing bank statement... This may take a minute.');
      setUploadDialogOpen(false);
      setUploadFile(null);

      // Poll for job completion
      const poll = async () => {
        for (let i = 0; i < 60; i++) { // poll up to 5 minutes
          await new Promise(r => setTimeout(r, 5000));
          try {
            const jobRes = await accountingApi.getJobStatus(jobId);
            const job = jobRes.data;
            if (job.status === 'completed') {
              toast.success(`Done! ${job.total_count} transactions extracted.`);
              loadData();
              setUploading(false);
              return;
            }
            if (job.status === 'failed') {
              toast.error(`Processing failed: ${job.error || 'Unknown error'}`);
              setUploading(false);
              return;
            }
          } catch { /* keep polling */ }
        }
        toast.error('Processing timed out. Please try again.');
        setUploading(false);
      };
      poll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to upload statement');
      setUploading(false);
    }
  };

  const handleCategorize = async (txnId, category) => {
    try {
      await accountingApi.updateTransaction(txnId, { category });
      setTransactions(prev => prev.map(t => t.id === txnId ? { ...t, category } : t));
      toast.success('Category updated');
    } catch { toast.error('Failed to update'); }
  };

  const handleCreateCategory = async () => {
    if (!newCatName.trim()) return;
    try {
      await accountingApi.createCategory({ name: newCatName, type: 'expense' });
      toast.success(`Category "${newCatName}" created`);
      setNewCatName('');
      setCatDialogOpen(false);
      const catRes = await accountingApi.getCategories();
      setCategories(catRes.data);
    } catch { toast.error('Failed to create'); }
  };

  const periodLabel = period === 'monthly' ? MONTHS[month - 1] + ' ' + year
    : period === 'quarterly' ? 'Q' + quarter + ' ' + year
    : period === 'yearly' ? String(year)
    : 'Year to Date ' + year;

  if (loading && !summary) {
    return <div className="space-y-6" data-testid="accounting-loading"><Skeleton className="h-8 w-48" /><div className="grid grid-cols-4 gap-4">{[1,2,3,4].map(i=><Skeleton key={i} className="h-28" />)}</div></div>;
  }

  const s = summary || {};

  return (
    <div className="space-y-4 animate-fade-in" data-testid="accounting-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-manrope font-bold">Accounting</h1>
          <p className="text-muted-foreground text-sm">{periodLabel}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="h-8 w-[120px] text-xs" data-testid="period-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="monthly">Monthly</SelectItem>
              <SelectItem value="quarterly">Quarterly</SelectItem>
              <SelectItem value="ytd">Year to Date</SelectItem>
              <SelectItem value="yearly">Yearly</SelectItem>
            </SelectContent>
          </Select>
          {period === 'monthly' && (
            <Select value={month.toString()} onValueChange={v => setMonth(parseInt(v))}>
              <SelectTrigger className="h-8 w-[110px] text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>{MONTHS.map((m, i) => <SelectItem key={i} value={(i+1).toString()}>{m}</SelectItem>)}</SelectContent>
            </Select>
          )}
          {period === 'quarterly' && (
            <Select value={quarter.toString()} onValueChange={v => setQuarter(parseInt(v))}>
              <SelectTrigger className="h-8 w-[80px] text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>{[1,2,3,4].map(q => <SelectItem key={q} value={q.toString()}>Q{q}</SelectItem>)}</SelectContent>
            </Select>
          )}
          <Select value={year.toString()} onValueChange={v => setYear(parseInt(v))}>
            <SelectTrigger className="h-8 w-[80px] text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>{[currentYear, currentYear-1, currentYear-2].map(y => <SelectItem key={y} value={y.toString()}>{y}</SelectItem>)}</SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={() => setTab('bank')} data-testid="upload-statement-btn">
            <Upload className="h-4 w-4 mr-1" /> Bank Statement
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Card data-testid="card-revenue">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2"><TrendingUp className="h-4 w-4 text-green-600" /><span className="text-xs text-muted-foreground font-medium uppercase">Amazon Revenue</span></div>
            <p className="text-2xl font-bold text-green-700">{fmt$(s.total_revenue || 0)}</p>
            <p className="text-xs text-muted-foreground mt-1">{s.load_count || 0} loads</p>
          </CardContent>
        </Card>
        <Card data-testid="card-dispatch-fees">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2"><PiggyBank className="h-4 w-4 text-blue-600" /><span className="text-xs text-muted-foreground font-medium uppercase">Dispatch Fees (Profit)</span></div>
            <p className="text-2xl font-bold text-blue-700">{fmt$(s.total_dispatch_fees || 0)}</p>
            <p className="text-xs text-muted-foreground mt-1">Company revenue</p>
          </CardContent>
        </Card>
        <Card data-testid="card-driver-payout">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2"><Wallet className="h-4 w-4 text-slate-600" /><span className="text-xs text-muted-foreground font-medium uppercase">Driver Payouts</span></div>
            <p className="text-2xl font-bold text-slate-800 dark:text-slate-100">{fmt$(s.total_driver_payout || 0)}</p>
            <p className="text-xs text-muted-foreground mt-1">Net to drivers</p>
          </CardContent>
        </Card>
        <Card data-testid="card-deductions">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2"><Receipt className="h-4 w-4 text-slate-600" /><span className="text-xs text-muted-foreground font-medium uppercase">Deductions</span></div>
            <p className="text-2xl font-bold text-slate-800 dark:text-slate-100">{fmt$(s.total_deductions || 0)}</p>
            <p className="text-xs text-muted-foreground mt-1">{s.deduction_count || 0} entries</p>
          </CardContent>
        </Card>
      </div>

      {/* Expected Balance Banner */}
      <Card className="border-2 border-blue-200 dark:border-blue-800 bg-blue-50/50 dark:bg-blue-950/20">
        <CardContent className="p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <div>
            <p className="text-sm font-semibold text-blue-900 dark:text-blue-100">Expected Company Balance</p>
            <p className="text-xs text-blue-700 dark:text-blue-300">Dispatch fees collected — what should remain in the bank from dispatching</p>
          </div>
          <p className="text-3xl font-extrabold text-blue-800 dark:text-blue-100" data-testid="expected-balance">{fmt$(s.expected_company_balance || 0)}</p>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="w-full sm:w-auto">
          <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="units" data-testid="tab-units">Unit Breakdown</TabsTrigger>
          <TabsTrigger value="expenses" data-testid="tab-expenses">Expenses</TabsTrigger>
          <TabsTrigger value="bank" data-testid="tab-bank">Bank Reconciliation{transactions.length > 0 && <Badge variant="secondary" className="ml-1 text-[10px]">{transactions.length}</Badge>}</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-4">
          {/* Deduction Breakdown */}
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-base">Deduction Breakdown</CardTitle></CardHeader>
            <CardContent>
              {Object.keys(s.deduction_breakdown || {}).length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">No deductions for this period</p>
              ) : (
                <div className="space-y-2">
                  {Object.entries(s.deduction_breakdown || {}).sort((a,b) => b[1]-a[1]).map(([type, amount]) => (
                    <div key={type} className="flex items-center justify-between py-1.5 border-b last:border-0">
                      <span className="text-sm capitalize">{type.replace(/_/g, ' ')}</span>
                      <span className="text-sm font-semibold">{fmt$(amount)}</span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* P&L Summary Table */}
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-base">Profit & Loss Summary</CardTitle></CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableBody>
                  <TableRow className="bg-green-50/50 dark:bg-green-950/20">
                    <TableCell className="font-medium">Amazon Revenue (Gross Payouts)</TableCell>
                    <TableCell className="text-right font-semibold text-green-700">{fmt$(s.total_revenue || 0)}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className="font-medium pl-8">− Driver Net Payouts</TableCell>
                    <TableCell className="text-right text-slate-700 dark:text-slate-200">−{fmt$(s.total_driver_payout || 0)}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className="font-medium pl-8">− Deductions (Fuel, Insurance, etc.)</TableCell>
                    <TableCell className="text-right text-slate-700 dark:text-slate-200">−{fmt$(s.total_deductions || 0)}</TableCell>
                  </TableRow>
                  <TableRow className="bg-blue-50/50 dark:bg-blue-950/20 border-t-2">
                    <TableCell className="font-bold">= Dispatch Fee Revenue (Company Profit)</TableCell>
                    <TableCell className="text-right font-bold text-blue-700 text-lg">{fmt$(s.total_dispatch_fees || 0)}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className="font-medium pl-8">− Company Expenses (office, parking, etc.)</TableCell>
                    <TableCell className="text-right text-rose-700">
                      −{fmt$(expensesTotal)}
                      <span className="text-[10px] text-muted-foreground ml-2">(see Expenses tab)</span>
                    </TableCell>
                  </TableRow>
                  <TableRow className="bg-emerald-50/60 dark:bg-emerald-950/20 border-t-2">
                    <TableCell className="font-bold">= Net Company Profit (after expenses)</TableCell>
                    <TableCell className="text-right font-bold text-emerald-700 text-lg">
                      {fmt$((s.total_dispatch_fees || 0) - expensesTotal)}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Unit Breakdown Tab */}
        <TabsContent value="units" className="space-y-3">
          <OrphanLoadsPanel onChanged={loadData} />
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Unit</TableHead>
                    <TableHead className="text-right">Loads</TableHead>
                    <TableHead className="text-right">Gross Payout</TableHead>
                    <TableHead className="text-right">Dispatch Fee</TableHead>
                    <TableHead className="text-right">Driver Net</TableHead>
                    <TableHead className="text-right">Deductions</TableHead>
                    <TableHead className="w-10 text-right"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {unitBreakdown.length === 0 ? (
                    <TableRow><TableCell colSpan={7} className="text-center py-8 text-muted-foreground">No data for this period</TableCell></TableRow>
                  ) : (
                    <>
                      {unitBreakdown.map(u => (
                        <TableRow key={u.unit_id} data-testid={`unit-row-${u.unit_id}`}>
                          <TableCell className="font-medium"><div className="flex items-center gap-1.5"><Truck className="h-3.5 w-3.5 text-muted-foreground" />{u.unit_number}</div></TableCell>
                          <TableCell className="text-right">{u.load_count}</TableCell>
                          <TableCell className="text-right">{fmt$(u.gross_payout)}</TableCell>
                          <TableCell className="text-right text-blue-700 font-medium">{fmt$(u.dispatch_fees)}</TableCell>
                          <TableCell className="text-right text-slate-700 dark:text-slate-200">{fmt$(u.driver_net)}</TableCell>
                          <TableCell className="text-right text-slate-700 dark:text-slate-200">{fmt$(u.deductions)}</TableCell>
                          <TableCell className="text-right">
                            {u.unit_id ? (
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-7 text-xs"
                                onClick={() => { setCsUnit(u); setCsOpen(true); }}
                                title="Build a Custom Settlement for this unit"
                                data-testid={`custom-settlement-btn-${u.unit_id}`}
                              >
                                <FileEdit className="h-3 w-3 sm:mr-1" />
                                <span className="hidden sm:inline">Custom</span>
                              </Button>
                            ) : (
                              <span className="text-[10px] text-muted-foreground italic" title="Loads with no assigned unit">unassigned</span>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                      <TableRow className="bg-muted/30 font-bold border-t-2">
                        <TableCell>Total</TableCell>
                        <TableCell className="text-right">{unitBreakdown.reduce((a,u)=>a+u.load_count,0)}</TableCell>
                        <TableCell className="text-right">{fmt$(unitBreakdown.reduce((a,u)=>a+u.gross_payout,0))}</TableCell>
                        <TableCell className="text-right text-blue-700">{fmt$(unitBreakdown.reduce((a,u)=>a+u.dispatch_fees,0))}</TableCell>
                        <TableCell className="text-right text-slate-700 dark:text-slate-200">{fmt$(unitBreakdown.reduce((a,u)=>a+u.driver_net,0))}</TableCell>
                        <TableCell className="text-right text-slate-700 dark:text-slate-200">{fmt$(unitBreakdown.reduce((a,u)=>a+u.deductions,0))}</TableCell>
                        <TableCell />
                      </TableRow>
                    </>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Expenses Tab */}
        <TabsContent value="expenses" className="space-y-4">
          <ExpensesTab
            startDate={summary?.start_date}
            endDate={summary?.end_date}
            onTotalChange={setExpensesTotal}
          />
        </TabsContent>

        {/* Bank Reconciliation Tab — Phase 1: full-featured, multi-format */}
        <TabsContent value="bank" className="space-y-4">
          <BankReconciliation />
        </TabsContent>
      </Tabs>

      {/* Add Category Dialog */}
      <Dialog open={catDialogOpen} onOpenChange={setCatDialogOpen}>
        <DialogContent className="sm:max-w-[350px]">
          <DialogHeader><DialogTitle>Add Expense Category</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5"><Label>Category Name</Label><Input value={newCatName} onChange={e => setNewCatName(e.target.value)} placeholder="e.g., Tolls, Permits" data-testid="new-cat-input" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCatDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleCreateCategory} disabled={!newCatName.trim()} className="bg-blue-600 hover:bg-blue-700" data-testid="create-cat-btn">Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Custom Settlement Dialog — opened from Unit Breakdown */}
      <CustomSettlementDialog open={csOpen} onOpenChange={setCsOpen} unit={csUnit} />
    </div>
  );
}
